import Big from 'big.js'
import I18n from './I18n'

const safeBig = value => {
  try {
    return Big(value)
  } catch (error) {
    return null
  }
}

export const PLEDGE_MAX = 4999999

const Validation = (_i18n, currency) => {
  const i18n = I18n({ ..._i18n, messages: _i18n.messages.errors })

  // Per-call errors object, reassigned at the top of every
  // validate(). Declared with `let` at factory scope so addError
  // and the Validations rule map (both private to this factory)
  // can close over a single binding that gets refreshed each call,
  // rather than being rebuilt themselves per call.
  //
  // Previously `const errors = {}` here, which accumulated entries
  // across consecutive validate() calls — a stale "invalid email"
  // from the first attempt would still be in the errors object on
  // the second attempt even after the donor fixed the field. The
  // fix is the reassignment inside validate() below.
  let errors

  const addError = (field, error) => {
    errors[field] = errors[field] || []
    errors[field].push(error)
  }

  const t = (message, key, data) => i18n.t(message || key, data) || message

  const Validations = {
    required (field, value, rule) {
      if (rule.value === false) return
      if (!(value && value.toString().trim()))
        addError(field, t(rule.message, 'blank'))
    },

    minLength (field, value, rule) {
      if (value && value.length < rule.value)
        addError(field, t(rule.message, 'too_short', { min: rule.value }))
    },

    maxLength (field, value, rule) {
      if (value && value.length > rule.value)
        addError(field, t(rule.message, 'too_long', { max: rule.value }))
    },

    format (field, value, rule) {
      const regex = rule.regex || rule.value
      if (value && !regex.test(value))
        addError(field, t(rule.message, 'invalid'))
    },

    email (field, value, rule) {
      if (!value || rule.value === false) return

      const email = value.trim()
      if (email.length > 254) addError(field, i18n.t('too_long', { max: 254 }))
      if (email.includes('..')) addError(field, i18n.t('email.double_dots'))

      const parts = email.split('@')
      if (parts.length != 2) {
        addError(field, i18n.t('email.one_at_symbol'))
        return
      }

      const [ local, domain ] = parts
      if (!local.length) addError(field, i18n.t('invalid'))
      if (local.length > 64) addError(field, i18n.t('email.local_too_long'))
      if (local.startsWith('.')) addError(field, i18n.t('email.local_starts_with_dot'))
      if (local.endsWith('.')) addError(field, i18n.t('email.local_ends_with_dot'))
      if (domain.startsWith('.')) addError(field, i18n.t('email.domain_starts_with_dot'))
      if (domain.endsWith('.')) addError(field, i18n.t('email.domain_ends_with_dot'))

      const tldRegex = /\.(?:[a-z]{2,}|xn--[a-z0-9]{2,})$/i
      if (!tldRegex.test(domain)) addError(field, i18n.t('email.invalid_tld'))
    },

    min (field, value, rule) {
      if (safeBig(value)?.lt(rule.value))
        addError(field, t(rule.message, 'too_low', { min: i18n.formatCurrency(rule.value, currency) }))
    },

    max (field, value, rule) {
      if (safeBig(value)?.gt(rule.value))
        addError(field, t(rule.message, 'too_high', { max: i18n.formatCurrency(rule.value, currency) }))
    },

    lambda (field, value, rule) {
      if (!rule.value)
        addError(field, t(rule.message, 'invalid'))
    }
  }

  return {
    validate (props, validators) {
      errors = {}

      const resolveRule = rule => {
        if (typeof rule == 'function') rule = rule(props)
        if (typeof rule != 'object' || rule instanceof Big)
          rule = { value: rule }
        return rule
      }

      if (validators.fieldSet)
        Object.keys(validators.fieldSet).forEach(field => {
          const value = props[field]
          const rules = validators.fieldSet[field]

          Object.keys(rules).forEach(ruleName => {
            const rule = resolveRule(rules[ruleName])
            Validations[ruleName](field, value, rule)
          })
        })

      if (validators.custom)
        validators.custom.forEach(Validator => {
          const validator = Validator(i18n, addError)
          validator.validate(props)
        })

      return { errors, valid: !Object.keys(errors).length }
    }
  }
}

export default Validation
